import { z } from 'zod';
import { inferApiResultSchema } from './infer-api';

/**
 * The guided submission-to-ready path shown on the demand details card. The step identifier is
 * stored as a varchar rather than a `pgEnum`, so retiring a step later does not require recreating a
 * pg enum type and rewriting the column. The writable vocabulary is enforced twice: a generated CHECK
 * constraint at the database layer (`demand_checklist_items_step_check`, generated from this same
 * list) and `DemandChecklistStepSchema`/the `DemandChecklistStep` TS union in application code.
 *
 * Deliberately excludes `customer-impact` (the sixth demand panel tab): that tab has no real data
 * model yet, only a placeholder, so there is nothing for a backend routine to derive completion
 * from.
 */
export const DEMAND_CHECKLIST_STEPS = [
  'complete-details',
  'evidence',
  'work-items',
  'reviews',
  'linked-ideas',
] as const;
export type DemandChecklistStep = (typeof DEMAND_CHECKLIST_STEPS)[number];

export const DemandChecklistStepSchema = z.enum(DEMAND_CHECKLIST_STEPS);

/**
 * A step's completion is written by whichever backend routine owns the underlying data (adding an
 * attachment, recording a review decision, linking an idea, ...) - the one exception is `evidence`,
 * which additionally has two explicit actions (`markDemandEvidenceSufficient`/`Insufficient`) since
 * an owner/assignee has to actively vouch for it. Either way, there is no generic writable DTO
 * here: a step's value is never taken from an arbitrary client body.
 *
 * `id`/`createdAt`/`updatedAt`/`completedAt`/`completedBy` are nullable because a step with no row
 * yet (nothing has evaluated it as complete) is reported as incomplete rather than pre-seeded -
 * `null` means "never persisted", not a sentinel value standing in for a real row.
 *
 * `step` is a plain string on the way out, never `DemandChecklistStepSchema`: a value that has
 * stopped being accepted on new records must still deserialise on rows already carrying it, and
 * narrowing this is what makes a response fail to validate against its own schema - see the
 * identical reasoning on `DemandReviewerDecisionSchema.decision`. The risk is no longer
 * hypothetical here even with the CHECK constraint above: a rolled-back deploy that briefly added a
 * sixth step can still leave a row this schema must be able to read back.
 */
export const DemandChecklistItemSchema = z.object({
  id: z.number().nullable(),
  accountId: z.string().max(50),
  demandId: z.number(),
  step: z.string(),
  isComplete: z.boolean(),
  completedBy: z.string().max(50).nullable(),
  completedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
});
export type DemandChecklistItemDto = z.infer<typeof DemandChecklistItemSchema>;

// GET /api/v1/fim/demands/:demandId/checklist
export const GetDemandChecklistResponseSchema = inferApiResultSchema(
  z.array(DemandChecklistItemSchema),
);
export type GetDemandChecklistResponseDto = z.infer<typeof GetDemandChecklistResponseSchema>;

// PUT /api/v1/fim/demands/:demandId/checklist/evidence/mark-sufficient
export const MarkDemandEvidenceSufficientResponseSchema =
  inferApiResultSchema(DemandChecklistItemSchema);
export type MarkDemandEvidenceSufficientResponseDto = z.infer<
  typeof MarkDemandEvidenceSufficientResponseSchema
>;

// PUT /api/v1/fim/demands/:demandId/checklist/evidence/mark-insufficient
export const MarkDemandEvidenceInsufficientResponseSchema =
  inferApiResultSchema(DemandChecklistItemSchema);
export type MarkDemandEvidenceInsufficientResponseDto = z.infer<
  typeof MarkDemandEvidenceInsufficientResponseSchema
>;
